/**
 * ENG-8346 — making a direct-chat cursor shortfall visible beyond one host.
 *
 * `classifyCursorAdvance` (./cursor-advance.js) gives every call site a verdict,
 * and each site writes a stderr/log warn on a shortfall. That warn is necessary
 * but NOT sufficient: it lands in `~/.augmented/<codeName>/*-stderr.log` (or
 * `manager.log`), which is tail-able but not monitored. The diagnostics runbook
 * only sends an operator to a channel-stderr log when it is "growing rapidly" —
 * and a slow trickle of stranded messages is precisely the shape nobody looks
 * for. That is how the ENG-8337 strand survived unnoticed in the first place.
 *
 * So the shortfall also rides the responsiveness-probe rail as a per-agent
 * classification counter, reaching CloudWatch as
 * `Augmented/Agents DirectChatCursorShortfallClassifications`. That metric — not
 * the log — is what makes the deferred bookkeeping decision (ENG-8347)
 * decidable, because it has a denominator and a fleet-wide view.
 *
 * ## ENG-8393 — that denominator claim was false when written
 *
 * As shipped, this module recorded ONLY the shortfall arm. The happy path wrote
 * nothing: not to the counter file, not to CloudWatch. So the metric had the
 * fleet-wide view but no denominator at all, and "no shortfalls happened" and
 * "the pipeline never ingested" produced byte-identical state — the metric
 * simply did not exist in either case. A fleet-wide zero on the read date would
 * have been unreadable, which for a metric whose entire purpose is to gate a
 * decision means the decision gets taken blind.
 *
 * Worth being precise about how the guards missed it, because everything they
 * check was correct: both writers, the CLI reader, the API fold and both
 * `KNOWN_CURSOR_SHORTFALL_REASONS` allowlists were in parity the whole time.
 * `scripts/check-classification-allowlist-parity.mjs` catches a key dropped by
 * ONE allowlist; it cannot catch a metric that never appears at all. Same
 * clean-zero failure as ENG-7716 and ENG-8214, one level up.
 *
 * `recordCursorAdvanceOutcome` below now records every verdict, so the sentence
 * above is true rather than aspirational. The cost is real and deliberate: an
 * agent that replies normally now writes a small counter file it previously did
 * not, which is the price of the zero being evidence.
 *
 * ## The two-allowlist trap this participates in
 *
 * Every counter that reaches CloudWatch as a dimension passes TWO independent
 * allowlists: the CLI reader (`readAndResetDirectChatCursorShortfallClassifications`
 * in apps/cli/src/lib/responsiveness-probe.ts) and the API probe handler
 * (packages/api/src/routes/host-runtime.ts). A key present in only one is dropped
 * with no error and no log, and the metric then reads a clean zero meaning
 * "never ingested". Because this counter exists specifically to gate a decision,
 * that zero would read as "no shortfalls, safe to leave the bookkeeping alone"
 * when nothing was ever measured. ENG-7716 and ENG-8214 are the two prior
 * instances; `scripts/check-classification-allowlist-parity.mjs` is the guard.
 * `CURSOR_SHORTFALL_REASONS` in ./cursor-advance.js is the third copy, and the
 * source of truth for the other two.
 *
 * ## File contract
 *
 * Mirrors `recordHotThreadClassification` (packages/mcp/src/slack-hot-thread-
 * telemetry.ts) exactly: one `<source>-cursor-advance-classifications.json` per
 * writer in the agent home, a flat `Record<key, count>`, synchronous best-effort
 * read-modify-write. The reader sums across every file matching the suffix and
 * resets by rename, so a writer racing a reset carries into the next window.
 *
 * `source` exists because there are TWO writers, in two different processes: the
 * direct-chat channel MCP (Claude Code agents) and the manager's ADR-0047
 * opencode arm. In practice an agent has exactly one of them — but that is a
 * behavioural invariant, not a structural one, and two processes doing
 * read-modify-write on one file lose increments. Separate files make the
 * separation structural for the cost of a filename.
 *
 * Lives in @augmented/core because both writers need it and `apps/cli` cannot
 * import from `packages/mcp`. It uses `node:fs`, so it is reachable ONLY via the
 * `@augmented/core/direct-chat/cursor-advance-telemetry.js` subpath and is
 * deliberately absent from the main barrel (the webapp imports that barrel).
 */
import { readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

import {
  cursorAdvanceCounterKey,
  type CursorAdvanceRoute,
  type CursorAdvanceVerdict,
} from './cursor-advance.js';

/** Per-writer counter filename suffix. */
export const CURSOR_SHORTFALL_COUNTER_SUFFIX = '-cursor-advance-classifications.json';

/**
 * Which process is writing. Becomes the counter-file prefix, so it must be
 * filename-safe; the union keeps that true by construction.
 *
 * - `direct-chat`         — the in-session direct-chat channel MCP (8 sites).
 * - `direct-chat-manager` — the manager's opencode arm (ADR-0047, 1 site).
 */
export type CursorShortfallSource = 'direct-chat' | 'direct-chat-manager';

export type CursorShortfallCounts = Record<string, number>;

/**
 * Record one cursor advance — shortfall, failure or success alike (ENG-8393).
 *
 * Takes the VERDICT rather than a pre-chosen key, so a call site cannot record
 * the wrong outcome or silently skip a non-shortfall one. `cursorAdvanceCounterKey`
 * owns that mapping and returns `null` for the one case worth skipping (an
 * advance that asked for no ids at all).
 *
 * Best-effort and non-throwing in every branch: telemetry that can fail a reply
 * is worse than telemetry that under-counts. No-op when `agentDir` is unset (a
 * host that never resolved an agent home writes nothing rather than guessing a
 * path).
 */
export function recordCursorAdvanceOutcome(
  agentDir: string | null | undefined,
  source: CursorShortfallSource,
  route: CursorAdvanceRoute,
  verdict: CursorAdvanceVerdict,
): void {
  if (!agentDir) return;
  const key = cursorAdvanceCounterKey(route, verdict);
  if (key === null) return;
  const path = join(agentDir, `${source}${CURSOR_SHORTFALL_COUNTER_SUFFIX}`);

  const counts: CursorShortfallCounts = {};
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as unknown;
    // Require a PLAIN (non-array) object: `[]` is typeof 'object', but a string
    // key assigned to an array is dropped by JSON.stringify, which would
    // silently lose every increment thereafter. Retain only non-negative integer
    // counts, so a corrupt string value cannot turn the next `+ 1` into string
    // concatenation.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'number' && Number.isInteger(v) && v >= 0) counts[k] = v;
      }
    }
  } catch {
    /* missing / corrupt → start fresh */
  }

  counts[key] = (counts[key] ?? 0) + 1;

  try {
    writeFileSync(path, JSON.stringify(counts), { mode: 0o600 });
  } catch {
    /* best-effort — a telemetry under-count is acceptable, never fatal */
  }
}
